/**
 * Where the caret is, in terms the document understands.
 *
 * A caret names a part, a bar, a voice and a moment. Everything the entry state machine
 * does starts by turning that into the actual part, bar and voice, and ends by turning a
 * new list of events back into an edit of that bar. Both are here so that the state machine
 * itself is about what a keystroke means rather than about looking things up.
 */

import type { Command } from "../commands/types";
import type { MeasureId, PartId } from "../document/ids";
import { barStep } from "../document/multirest";
import { capacityOf, eventOnsets, measureIndexOf, occupiedTicks } from "../document/query";
import type { MusicEvent, Part, PartMeasure, Score, Voice } from "../document/types";
import { sameVoice } from "./voices";

/** Where the next note will be written: a moment in one voice of one bar of one part. */
export interface Caret {
  readonly partId: PartId;
  readonly measureId: MeasureId;
  readonly voiceIndex: number;
  /** Ticks from the start of the bar. */
  readonly onset: number;
}

/** The part, bar, voice and bar length a caret stands in. */
export interface Place {
  readonly part: Part;
  readonly measure: PartMeasure;
  readonly voice: Voice;
  readonly capacity: number;
}

/** What the caret points at, or a sentence saying why it points at nothing. */
export function placeOf(score: Score, caret: Caret): Place | string {
  const part = score.parts.find((candidate) => candidate.id === caret.partId);
  if (part === undefined) return "that part is not in the score";
  const measure = part.measures[caret.measureId];
  if (measure === undefined) return "that part has nothing for that bar";
  const voice = measure.voices[caret.voiceIndex];
  if (voice === undefined) return "that bar has no such voice";
  const index = measureIndexOf(score, caret.measureId);
  if (index < 0) return "that bar is not in the score";
  return { part, measure, voice, capacity: capacityOf(score, index) };
}

/**
 * Replace one voice of a bar, as the single command that describes a write.
 *
 * Every edit note entry makes is one of these. Writing a note can add rests before it,
 * shorten the note it lands on and leave silence after it, and calling that three commands
 * would be describing the machinery rather than what happened.
 */
export function rewrite(caret: Caret, place: Place, events: readonly MusicEvent[]): Command {
  const voices = place.measure.voices.map((voice, at) =>
    at === caret.voiceIndex ? { ...voice, events } : voice,
  );
  return {
    kind: "setPartMeasure",
    partId: caret.partId,
    measureId: caret.measureId,
    measure: { ...place.measure, voices },
  };
}

/** The index in its voice of the event sounding at a moment, or `-1` when none is. */
export function indexAt(voice: Voice, onset: number, capacity: number): number {
  const onsets = eventOnsets(voice);
  for (let at = voice.events.length - 1; at >= 0; at -= 1) {
    const event = voice.events[at];
    const start = onsets[at];
    if (event === undefined || start === undefined) continue;
    if (start <= onset && start + occupiedTicks(event, capacity) > onset) return at;
  }
  return -1;
}

/**
 * Where the caret goes after writing something `length` ticks long.
 *
 * Past the end of the bar it lands at the start of the next one. At the end of the piece
 * it stops at the bar line rather than wrapping or inventing a bar: adding a bar is an
 * edit, and typing a note is not the moment to make one without being asked.
 */
export function advanced(score: Score, caret: Caret, length: number, capacity: number): Caret {
  const onset = caret.onset + length;
  if (onset < capacity) return { ...caret, onset };
  return crossed(score, caret, 1) ?? { ...caret, onset: capacity };
}

/**
 * The caret at the start of the bar one step from its own, or `null` at either end.
 *
 * In a part that gathers its silent bars, a run of them is drawn as one bar and is one bar
 * to step through; {@link barStep} is what makes the caret come out the far side of a run
 * rather than stop inside it, where nothing is drawn. See `document/multirest.ts`.
 *
 * The voice is re-found rather than carried across. Bars hold different numbers of voices
 * -- a divided passage is divided only where it is divided -- so an index means a different
 * line of music from one bar to the next, and what the caret keeps is the staff it is on
 * and which voice of that staff it is in. See `editor/voices.ts`.
 */
function crossed(score: Score, caret: Caret, direction: number): Caret | null {
  const part = score.parts.find((candidate) => candidate.id === caret.partId);
  const from = measureIndexOf(score, caret.measureId);
  if (part === undefined || from < 0) return null;
  const global = score.measures[barStep(score, part, from + Math.sign(direction), direction)];
  const here = part.measures[caret.measureId];
  const into = global === undefined ? undefined : part.measures[global.id];
  if (global === undefined || here === undefined || into === undefined) return null;
  return {
    ...caret,
    measureId: global.id,
    voiceIndex: sameVoice(here, into, caret.voiceIndex),
    onset: 0,
  };
}

/** The caret one event forwards or backwards, across the bar line when it has to. */
export function stepped(score: Score, caret: Caret, delta: number): Caret | string {
  const place = placeOf(score, caret);
  if (typeof place === "string") return place;

  const onsets = eventOnsets(place.voice);
  const at = indexAt(place.voice, caret.onset, place.capacity);
  const onset = onsets[(at < 0 ? onsets.length : at) + Math.sign(delta)];
  if (onset !== undefined) return { ...caret, onset };

  const neighbour = crossed(score, caret, delta);
  if (neighbour === null) return "there is no bar that way";
  const into = placeOf(score, neighbour);
  if (typeof into === "string") return into;
  const theirs = eventOnsets(into.voice);
  return { ...neighbour, onset: delta < 0 ? (theirs[theirs.length - 1] ?? 0) : 0 };
}
